import { createTheme } from "@mui/material/styles";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

export function createAppTheme(mode) {
  const isDark = mode === "dark";

  return createTheme({
    palette: {
      mode,
      primary: {
        main: "#2563eb",
        light: "#60a5fa",
        dark: "#1d4ed8",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#3b82f6",
      },
      warning: {
        main: "#f59e0b",
      },
      error: {
        main: "#ef4444",
      },
      background: {
        default: isDark ? "#0f172a" : "#f8fafc",
        paper: isDark ? "#1e293b" : "#ffffff",
      },
    },
    typography: {
      fontFamily: "Roboto, Arial",
    },
    components: {
      MuiButton: {
        defaultProps: {
          variant: "outlined",
        },
        styleOverrides: {
          root: {
            borderRadius: "999px",
            textTransform: "uppercase",
            fontWeight: 700,
            letterSpacing: 0.8,
            transition: "all .18s ease",
            "&.Mui-disabled": {
              opacity: 0.48,
              boxShadow: "none",
            },
          },
          sizeSmall: {
            borderRadius: "999px",
            fontSize: 12,
          },
          sizeLarge: {
            borderRadius: "999px",
            fontSize: 15,
            paddingTop: 10,
            paddingBottom: 10,
          },
          contained: ({ theme, ownerState }) => {
            const key = ownerState.color ?? "primary";
            const semanticColors = ["error", "warning", "success", "info"];
            const isSemantic = semanticColors.includes(key);

            if (!isSemantic) {
              const isDark = theme.palette.mode === "dark";
              return {
                background: isDark ? "rgba(255,255,255,0.09)" : "#f9f9f9",
                color: isDark ? "rgb(255, 255, 255)" : "#1e293b",
                border: isDark
                  ? "1px solid rgba(255,255,255,0.13)"
                  : "1px solid rgba(0,0,0,0.10)",
                boxShadow: "none",
                "&:hover": {
                  background: isDark ? "rgba(255,255,255,0.14)" : "#f9f9f9",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 14px rgba(0,0,0,0.10)",
                },
                "&:active": {
                  transform: "translateY(0)",
                  boxShadow: "none",
                },
              };
            }

            const c = theme.palette[key]?.main ?? theme.palette.primary.main;
            return {
              boxShadow: `0 4px 14px ${c}38`,
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: `0 8px 22px ${c}50`,
              },
              "&:active": {
                transform: "translateY(0)",
                boxShadow: `0 2px 8px ${c}28`,
              },
            };
          },
          outlined: ({ theme, ownerState }) => {
            const key = ownerState.color ?? "primary";
            const c = theme.palette[key]?.main ?? theme.palette.primary.main;
            return {
              borderWidth: "1.5px",
              background:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.03)"
                  : "rgba(255,255,255,0.82)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              "&:hover": {
                borderWidth: "1.5px",
                background: `${c}12`,
                transform: "translateY(-1px)",
                boxShadow: `0 6px 18px ${c}28`,
              },
              "&:active": {
                transform: "translateY(0)",
              },
            };
          },
          text: ({ theme, ownerState }) => {
            const key = ownerState.color ?? "primary";
            const c = theme.palette[key]?.main ?? theme.palette.primary.main;
            return {
              "&:hover": {
                background: `${c}10`,
              },
            };
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: (themeParam) => {
          const isDarkMode = themeParam.palette.mode === "dark";
          const thumbColor = isDarkMode
            ? "rgba(255,255,255,0.28)"
            : "rgba(0,0,0,0.28)";
          const thumbHoverColor = isDarkMode
            ? "rgba(255,255,255,0.42)"
            : "rgba(0,0,0,0.42)";

          return {
            "*": {
              scrollbarWidth: "thin",
              scrollbarColor: "transparent transparent",
            },
            "*:hover": {
              scrollbarColor: `${thumbColor} transparent`,
            },
            "*::-webkit-scrollbar": {
              width: 10,
              height: 10,
            },
            "*::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "*::-webkit-scrollbar-thumb": {
              backgroundColor: "transparent",
              borderRadius: 8,
              border: "2px solid transparent",
              backgroundClip: "content-box",
            },
            "*:hover::-webkit-scrollbar-thumb": {
              backgroundColor: thumbColor,
            },
            "*::-webkit-scrollbar-thumb:hover": {
              backgroundColor: thumbHoverColor,
            },
          };
        },
        '[class*="MuiBox-root"]': {
          padding: 0,
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: "transparent",
            color: theme.palette.text.primary,
            borderRadius: 8,
            border: "none",
          }),
          columnHeaders: ({ theme }) => ({
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.03)",
            color: theme.palette.text.primary,
          }),
        },
      },
      MuiContainer: {
        styleOverrides: {
          maxWidthLg: {
            "@media (min-width: 1200px)": {
              maxWidth: "95% !important",
            },
          },
        },
      },
      MuiAlert: {
        defaultProps: {
          iconMapping: {
            success: <CheckCircleRoundedIcon fontSize="inherit" />,
            error: <ErrorOutlineRoundedIcon fontSize="inherit" />,
            warning: <WarningAmberRoundedIcon fontSize="inherit" />,
            info: <InfoOutlinedIcon fontSize="inherit" />,
          },
        },

        styleOverrides: {
          root: ({ ownerState }) => {
            const colorMap = {
              success: {
                bg: "rgba(46, 125, 50, 0.12)",
                border: "rgba(46, 125, 50, 0.35)",
                color: "#2e7d32",
              },
              error: {
                bg: "rgba(211, 47, 47, 0.12)",
                border: "rgba(211, 47, 47, 0.35)",
                color: "#d32f2f",
              },
              warning: {
                bg: "rgba(237, 108, 2, 0.12)",
                border: "rgba(237, 108, 2, 0.35)",
                color: "#ed6c02",
              },
              info: {
                bg: "rgba(2, 136, 209, 0.12)",
                border: "rgba(2, 136, 209, 0.35)",
                color: "#0288d1",
              },
            };

            const current = colorMap[ownerState.severity] || colorMap.info;

            return {
              borderRadius: 12,
              fontWeight: 600,
              background: current.bg,
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              border: `1px solid ${current.border}`,
              color: current.color,
              boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
              alignItems: "center",
              "& .MuiAlert-icon": {
                fontSize: 22,
                opacity: 0.9,
              },
              "& .MuiAlert-message": {
                padding: "2px 0",
              },
              "& .MuiAlert-action": {
                alignItems: "center",
              },
            };
          },
        },
      },
    },
  });
}
